import React from "react";

/**
 * Universal, deterministic Mojibake repair dictionary.
 * Maps mis-decoded CP437/Windows-1252 byte representation sequences back to native UTF-8 emojis/symbols.
 * Preserves clean UTF-8 text, emojis, punctuation, and non-English scripts completely untouched.
 */
const STRING_REPLACEMENTS = [
  ["Nature╬ô├ç├ûs", "Nature's"],
  ["╬ô├ç├û", "'"],
  ["╬ô├ç├┤", "—"],
  ["ΓÇô", "—"],
  ["╬ô┬ú┬┐", "✨"],
  ["Γ£¿", "✨"],
  ["┬¿", "✨"],
  ["╬ô┬ú├╢", "✔"],
  ["Γ£ï", "✔"],
  ["╬ô┬ú├á", "✅"],
  ["╬ô┬ú", "✅"],
  ["Γëí╞Æ├«ΓöÉ", "🌿"],
  ["Γëí╞Æ├«Γò¥", "🌿"],
  ["Γëí╞Æ├«Γòæ", "🌿"],
  ["Γëí╞Æ├«ΓûÆ", "🌿"],
  ["Γëí╞Æ├¼├½", "🌿"],
  ["Γëí╞Æ├«Γòò", "🌿"],
  ["Γëí╞Æ├╗├▒", "🌿"],
  ["Γëí╞Æ├¼├┤", "🍓"],
  ["Γëí╞Æ├¼├«", "🍉"],
  ["Γëí╞Æ├ª├½", "🌸"],
  ["╬ô├╗ΓòóΓê⌐Γòò├à", "🌸"],
  ["╬ô├╗", "🌸"],
  ["«öÉ", "🌸"],
  ["â€™", "'"],
  ["â€œ", '"'],
  ["â€", '"'],
  ["â€“", "—"],
  ["â€”", "—"],
  ["Ã©", "é"],
  ["Â", ""],
  ["ç╢", ""],
  ["å┬", ""],
  ["┬╝", ""],
  ["ï¿½", ""]
];

/**
 * Repairs Mojibake character corruptions safely without altering valid UTF-8 strings or valid emojis.
 */
export const cleanMojibake = (text) => {
  if (!text || typeof text !== "string") return text || "";

  let str = text;

  // Only repair string if it contains known Mojibake byte markers
  if (/[╬ôΓëíÃÂâð«»┬├╢çå]/.test(str)) {
    for (const [target, replacement] of STRING_REPLACEMENTS) {
      if (str.includes(target)) {
        str = str.split(target).join(replacement);
      }
    }

    // Fallback cleanup for any residual double-encoded CP437 sequences
    str = str.replace(/Γëí╞Æ[^\s]{0,6}/g, "🌿 ");
    str = str.replace(/╬ô[^\s]{0,6}/g, "");
    str = str.replace(/[ç╢å┬┬╝ï¿½]/g, "");
  }

  return str;
};

/**
 * Recursively normalizes all text fields in an API response object/array.
 */
export const normalizeApiData = (data) => {
  if (!data) return data;
  if (typeof data === "string") return cleanMojibake(data);
  if (Array.isArray(data)) return data.map(normalizeApiData);
  if (typeof data === "object") {
    const res = {};
    for (const key of Object.keys(data)) {
      res[key] = normalizeApiData(data[key]);
    }
    return res;
  }
  return data;
};

/**
 * Cleans product text and returns a string with line breaks preserved.
 * NEVER splits by period ('.').
 */
export const formatProductText = (text) => {
  if (!text) return "";
  return cleanMojibake(String(text)).trim();
};

/**
 * Formats multi-line product text into JSX element blocks with line breaks preserved.
 * NEVER splits by period ('.').
 */
export const formatProductTextJSX = (text, defaultPrefix = "") => {
  if (!text) return null;

  const cleaned = cleanMojibake(String(text)).trim();
  if (!cleaned) return null;

  // Preserve newlines (\n or \r\n or \r)
  const lines = cleaned.includes("\n")
    ? cleaned.split(/\r?\n/)
    : [cleaned];

  return lines
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      // Check if line already has a bullet/emoji prefix, keycap digit, or URL
      const hasPrefixOrUrl = /^(?:[•\-*✅🌸🌟👉➤🌿🧘💚🍉🍓⚡🥥🐝🌺🌾]|1️⃣|2️⃣|3️⃣|4️⃣|5️⃣|6️⃣|7️⃣|8️⃣|9️⃣|🔟|▶️|👉🏻|https?:\/\/|www\.)/u.test(line);
      const prefix = hasPrefixOrUrl ? "" : defaultPrefix;

      return (
        <div key={i} style={{ marginBottom: "4px" }}>
          {prefix}{line}
        </div>
      );
    });
};
